import { useId } from 'react'

import styled from '@emotion/styled'

import { SubtleExpandIcon } from '@arcade/web/icons/react'
import { ArcadeBorderedCard, spacing, Text } from '@cash-design-system/react'
import {
  FormBlockerElement,
  FormBlockerElementButtonElementSize,
  FormBlockerElementButtonElementStyle,
} from '@cash-web/protos/squareup/franklin/form_blocker.pb'
import { useTextTokenizer } from '@cash-web/shared-feature-use-text-tokenizer'

import { ButtonElement } from '../ButtonElement/ButtonElement'
import { ListUnorderedElement } from '../ListUnorderedElement/ListUnorderedElement'

export type CollapsibleRowElement = FormBlockerElement & {
  id: string
  cell_default_element: NonNullable<FormBlockerElement['cell_default_element']>
}

const Card = styled(ArcadeBorderedCard)`
  padding: ${spacing.l};
  margin: 0 ${spacing.xs};
`

const SectionTitle = styled(Text)`
  display: block;
  text-transform: uppercase;
  margin-bottom: ${spacing.s};
`

// ArcadeAccordion owns its expansion state; these rows share one state across cards.
const RowButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.s};
  width: 100%;
  padding: ${spacing.m} 0;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.arcadeColors.semantic.textStandard};
  text-align: left;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.arcadeColors.semantic.borderProminent};
    outline-offset: 2px;
    border-radius: ${({ theme }) => theme.border.radius.medium};
  }
`

const ExpandedContent = styled.div`
  padding: 0 0 ${spacing.m};
`

const BodyText = styled(Text)`
  white-space: pre-line;
`

const ExpandIcon = styled.span<{ $expanded: boolean }>`
  display: inline-flex;
  color: ${({ theme }) => theme.arcadeColors.semantic.iconExtraSubtle};
  transform: ${({ $expanded }) => ($expanded ? 'rotate(180deg)' : 'none')};
`

const Footer = styled.div`
  padding-top: ${spacing.m};
`

const CollapsibleRow = ({
  row,
  expanded,
  onToggle,
}: {
  row: CollapsibleRowElement
  expanded: boolean
  onToggle: () => void
}) => {
  const contentId = useId()
  const textTokenizer = useTextTokenizer({ textLink: { target: '_blank' } })
  const cell = row.cell_default_element.cell
  const list = row.cell_default_element.collapsible_presentation?.unordered_list
  const hasList = Boolean(list?.items?.length && list.items.every(item => item.label?.trim()))

  return (
    <div>
      <RowButton
        type="button"
        aria-expanded={expanded}
        aria-controls={expanded ? contentId : undefined}
        onClick={onToggle}
      >
        <Text typography="labelMedium" color="textStandard">
          {cell?.label_text?.translated_value}
        </Text>
        <ExpandIcon aria-hidden="true" $expanded={expanded}>
          <SubtleExpandIcon size={16} />
        </ExpandIcon>
      </RowButton>
      {expanded && (
        <ExpandedContent id={contentId}>
          {hasList && list ? (
            <ListUnorderedElement {...list} inset={false} />
          ) : (
            <BodyText typography="body" color="textStandard">
              {textTokenizer(cell?.body_text?.translated_value)}
            </BodyText>
          )}
        </ExpandedContent>
      )}
    </div>
  )
}

export const isCollapsibleRow = (element: FormBlockerElement | undefined): element is CollapsibleRowElement => {
  if (!element) return false
  const { cell_default_element: value, id } = element
  const { cell, collapsible_presentation: presentation } = value ?? {}
  return Boolean(
    id?.trim() &&
    presentation?.section_id?.trim() &&
    presentation.section_title?.translated_value?.trim() &&
    cell?.label_text?.translated_value?.trim() &&
    cell.body_text?.translated_value?.trim() &&
    !value?.cell_action &&
    !value?.selection_group_id &&
    !value?.initial_selection &&
    !cell.disabled &&
    !cell.icon &&
    !cell.accessory &&
    !cell.accessory_leading_image &&
    !cell.label_icon &&
    !cell.body_icon &&
    !cell.body_2_text &&
    !cell.is_badged
  )
}

export const CollapsibleSection = ({
  rows,
  expandedRowId,
  onExpandedRowChange,
}: {
  rows: CollapsibleRowElement[]
  expandedRowId: string | null
  onExpandedRowChange: (id: string | null) => void
}) => {
  const first = rows[0].cell_default_element.collapsible_presentation
  const footerAction = first?.footer_action

  return (
    <Card>
      <SectionTitle as="h2" typography="labelXSmall" color="textStandard">
        {first?.section_title?.translated_value}
      </SectionTitle>
      {rows.map(row => (
        <CollapsibleRow
          key={row.id}
          row={row}
          expanded={expandedRowId === row.id}
          onToggle={() => onExpandedRowChange(expandedRowId === row.id ? null : row.id)}
        />
      ))}
      {footerAction?.text && (
        <Footer>
          <ButtonElement
            action={footerAction}
            size={FormBlockerElementButtonElementSize.COMPACT}
            style={FormBlockerElementButtonElementStyle.SECONDARY}
          />
        </Footer>
      )}
    </Card>
  )
}
